import type { ReactNode } from 'react';
import type { QualificationQuestion, QualificationSection } from '../../lib/api';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { AlertTriangle, CheckCircle2, CircleDot, ClipboardList, Flag } from 'lucide-react';
import { QuestionInput } from '../qualification-detail/QuestionInput';
import type { ResponseMap } from '../qualification-detail/types';

/** Whether a check has an answer of any kind. */
export function hasAnswer(answer: ResponseMap[number] | undefined): boolean {
  if (!answer) return false;
  return Boolean(answer.text_value?.trim()) || (answer.number_value !== null && answer.number_value !== undefined)
    || Boolean(answer.date_value) || answer.boolean_value === true || answer.boolean_value === false
    || Boolean(answer.choice_value?.length) || Boolean(answer.file_value);
}

type Tone = 'finding' | 'answered' | 'missing' | 'open';

const TONE: Record<Tone, { edge: string; card: string; badge: string }> = {
  finding: {
    edge: 'border-l-red-500',
    card: 'bg-red-50/60 dark:bg-red-950/20',
    badge: 'bg-red-600 text-white',
  },
  answered: {
    edge: 'border-l-emerald-500',
    card: 'bg-card',
    badge: 'bg-emerald-600 text-white',
  },
  missing: {
    edge: 'border-l-amber-400',
    card: 'bg-card',
    badge: 'bg-amber-100 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100',
  },
  open: {
    edge: 'border-l-slate-200 dark:border-l-slate-700',
    card: 'bg-card',
    badge: 'bg-muted text-muted-foreground',
  },
};

interface AuditChecklistProps {
  sections: QualificationSection[];
  answers: ResponseMap;
  canEdit: boolean;
  uploading: Record<number, boolean>;
  onAnswer: (questionId: number, value: string | number | boolean | string[], questionType: string) => void;
  onFileUpload: (questionId: number, file: File) => void;
  onFileDownload: (questionId: number) => void;
  onFileRemove: (questionId: number) => void;
  /** Comment, finding, photos and actions under each check. */
  renderFooter: (question: QualificationQuestion) => ReactNode;
}

/** "1. Factory Assessment" → { number: '1', title: 'Factory Assessment' }; unnumbered titles keep no number. */
export function splitSectionTitle(title: string): { number: string | null; title: string } {
  const match = /^\s*(\d+(?:\.\d+)*)[.)]?\s+(.+)$/.exec(title || '');
  return match ? { number: match[1] ?? null, title: match[2] ?? title } : { number: null, title };
}

function toneOf(question: QualificationQuestion, answer: ResponseMap[number] | undefined): Tone {
  if (answer?.flagged) return 'finding';
  if (hasAnswer(answer)) return 'answered';
  return question.is_required ? 'missing' : 'open';
}

/**
 * The audit form: each section a band with its own progress and findings, each
 * check a raised card whose left edge shows its state — green answered, red
 * finding, amber required and still open. Same inputs as the questionnaire
 * screen (touch-sized), so answers behave identically.
 */
export default function AuditChecklist({
  sections, answers, canEdit, uploading, onAnswer, onFileUpload, onFileDownload, onFileRemove, renderFooter,
}: AuditChecklistProps) {
  return (
    <div className="space-y-8">
      {sections.map((section) => {
        const heading = splitSectionTitle(section.title);
        const questions = [...(section.questions ?? [])].sort((a, b) => a.sort_order - b.sort_order);
        const answered = questions.filter((q) => hasAnswer(answers[q.id])).length;
        const findings = questions.filter((q) => answers[q.id]?.flagged).length;
        const percent = questions.length ? Math.round((answered * 100) / questions.length) : 0;
        return (
          <section key={section.id} id={`section-${section.id}`} className="scroll-mt-52 overflow-hidden rounded-2xl border bg-muted/30 shadow-sm">
            <header className="flex flex-wrap items-center justify-between gap-4 border-b bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-5 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground shadow">
                  {heading.number ?? <ClipboardList className="h-4 w-4" aria-hidden />}
                </span>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold leading-tight">{heading.title}</h2>
                  {section.description && <p className="text-sm text-muted-foreground">{section.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {findings > 0 && (
                  <Badge className="gap-1 border-0 bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200">
                    <Flag className="h-3 w-3" />{findings} {findings === 1 ? 'finding' : 'findings'}
                  </Badge>
                )}
                <div className="w-36">
                  <div className="mb-1 text-right text-xs text-muted-foreground">{answered} of {questions.length} answered</div>
                  <Progress value={percent} className="h-1.5" aria-label={`${section.title}: ${percent}% answered`} />
                </div>
              </div>
            </header>
            <div className="space-y-4 p-4 sm:p-5">
              {questions.map((question, index) => {
                const answer = answers[question.id];
                const tone = toneOf(question, answer);
                const style = TONE[tone];
                return (
                  <article key={question.id} id={`question-${question.id}`}
                    className={`scroll-mt-52 rounded-xl border border-l-4 ${style.edge} ${style.card} p-4 shadow-sm transition-shadow hover:shadow-md sm:p-5`}
                    data-tone={tone}>
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 flex h-7 min-w-[2.25rem] shrink-0 items-center justify-center rounded-full px-2 text-xs font-semibold ${style.badge}`}>
                        {heading.number ? `${heading.number}.${index + 1}` : index + 1}
                      </span>
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <h3 id={`question-${question.id}-label`} className="text-base font-semibold leading-snug">
                            {question.question_text}
                          </h3>
                          <span className="flex shrink-0 items-center gap-2">
                            {question.is_required && tone === 'missing' && (
                              <Badge variant="outline" className="border-amber-300 text-amber-800 dark:border-amber-700 dark:text-amber-200">Required</Badge>
                            )}
                            {tone === 'answered' && <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-label="Answered" />}
                            {tone === 'finding' && <AlertTriangle className="h-5 w-5 text-red-600" aria-label="Finding" />}
                            {tone === 'open' && <CircleDot className="h-4 w-4 text-muted-foreground" aria-label="Optional" />}
                          </span>
                        </div>
                        {question.help_text && <p className="text-sm text-muted-foreground">{question.help_text}</p>}
                      </div>
                    </div>
                    <div className="mt-4 sm:pl-12">
                      <QuestionInput question={question} responses={answers} qualificationStatus={canEdit ? 'in_progress' : 'approved'}
                        isSupplier={false} canEdit={canEdit} touch onResponseChange={onAnswer}
                        onFileUpload={onFileUpload} onFileDownload={onFileDownload} onFileRemove={onFileRemove}
                        isUploadingFile={uploading[question.id]} />
                    </div>
                    <div className="mt-4 border-t pt-3 sm:pl-12">{renderFooter(question)}</div>
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
